/**
 * Superall API client primitives.
 *
 * Centralizes the API base URL, Superall JWT injection, and 401-handling
 * for every fetch the consumer shell makes. Extracted from the monolithic
 * `App.tsx` per ticket superall-shell-01.
 *
 * Auth model: the Superall access token lives in `localStorage` under
 * `superall_token` (managed by `AuthProvider`). Every call goes through
 * `apiFetch`, which injects the Bearer header. On a 401 response the
 * helper dispatches a `superall:auth-expired` window event; the
 * AuthProvider listens for it and bounces the user to `/login`.
 *
 * Per R7: response bodies and headers may carry PII; do NOT log them
 * unmasked. The helper deliberately surfaces non-2xx errors to the
 * caller and stays silent at the transport layer.
 */

export const API_BASE =
  import.meta.env.VITE_API_URL ?? "http://localhost:3000/api";

export const PLATFORM_API = `${API_BASE}/platform/v1`;

const TOKEN_KEY = "superall_token";

export const AUTH_EXPIRED_EVENT = "superall:auth-expired";

/**
 * Read the Superall access token directly from localStorage. The
 * AuthProvider mirrors this value into React state — we deliberately
 * round-trip through storage in `apiFetch` so that helpers called
 * outside the React tree (e.g. event handlers, BU handoff) still pick
 * up the freshest token after a silent refresh.
 */
export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export interface ApiFetchOptions extends RequestInit {
  /** When true, skip the Authorization header (e.g. login/refresh). */
  skipAuth?: boolean;
  /** When true, suppress the auth-expired event on 401. */
  silentOn401?: boolean;
}

/**
 * Authenticated fetch wrapper.
 *
 * `path` may be an absolute URL (used as-is) or a path beginning with
 * `/` (joined to API_BASE) or any other relative form (also joined to
 * API_BASE). On 401 we dispatch a window event so the AuthProvider can
 * clear the session and route to `/login` without forcing every caller
 * to handle it.
 */
export async function apiFetch(
  path: string,
  init: ApiFetchOptions = {},
): Promise<Response> {
  const { skipAuth, silentOn401, headers, ...rest } = init;

  const url = path.startsWith("http")
    ? path
    : path.startsWith("/") && path.startsWith("/api/")
      ? path
      : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

  const finalHeaders = new Headers(headers);
  if (!skipAuth) {
    const token = getStoredToken();
    if (token && !finalHeaders.has("Authorization")) {
      finalHeaders.set("Authorization", `Bearer ${token}`);
    }
  }
  if (
    rest.body &&
    !finalHeaders.has("Content-Type") &&
    typeof rest.body === "string"
  ) {
    finalHeaders.set("Content-Type", "application/json");
  }

  const res = await fetch(url, { ...rest, headers: finalHeaders });

  if (res.status === 401 && !silentOn401 && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }

  return res;
}

/**
 * Convenience JSON GET against the platform API. Throws on non-2xx so
 * react-query / hook callers can treat the result as the success type.
 */
export async function apiGetJSON<T>(
  path: string,
  init?: ApiFetchOptions,
): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`GET ${path} failed (${res.status}): ${text.slice(0, 120)}`);
  }
  return (await res.json()) as T;
}
